import Vue from 'vue'

import { ApiClient } from '@/api/client.js'
import searchClient from '@/api/trackSearch.js'
import { normalizeTrackSearchResult } from '@/utils/catalogue.js'

const client = new ApiClient()

// mutation types
const SET_SEARCH_OPTIONS = 'SET_SEARCH_OPTIONS'
const SET_RESULTS = 'SET_RESULTS'
const SET_NEXT_PAGE_URL = 'SET_NEXT_PAGE_URL'
const APPEND_RESULTS = 'APPEND_RESULTS'
const SET_SEARCH_PARAMS = 'SET_SEARCH_PARAMS'
const SET_A_SEARCH_PARAM = 'SET_A_SEARCH_PARAM'
const APPEND_A_SEARCH_PARAM = 'APPEND_A_SEARCH_PARAM'
const SET_LOADING = 'SET_LOADING'
const SET_LOADING_NEW_SEARCH = 'SET_LOADING_NEW_SEARCH'

// initial state
const state = {
  results: [],
  loading: false,
  loadingNewSearch: true, // So we don't get a glimpse of the 'no tracks found' message on the initial load of the Search page.
  nextPageUrl: null,
  searchOptions: [], // The filters the user can control.
  searchParams: { // The filters the user HAS selected.
    // Other param fields will be added dynamically.
    text: []
  }
}

function cleanTempo (arr) {
  if (arr.length === 0) {
    return arr
  }
  const SLOW = 0
  const MEDIUM = 1
  const FAST = 2
  const numberToValue = {
    [SLOW]: 'Slow',
    [MEDIUM]: 'Medium',
    [FAST]: 'Fast'
  }

  const num1 = arr[0]
  const num2 = arr[1]
  const val1 = numberToValue[num1]
  const val2 = numberToValue[num2]

  let activeValues = []

  // If we are missing tempos in the database then when searching for all tempos we
  // want to pass no tempo search options to the API (otherwise those with missing
  // tempos will not show up).
  if (num1 === SLOW && num2 === FAST) {
    // Redundant but want to show that Slow to Fast means not sending any values.
    activeValues = []
  } else if (num1 === num2) {
    // Two numbers are same, set one option, clear other two.
    activeValues = [val1]
  } else {
    activeValues = [val1, val2]
  }

  return activeValues
}

// getters
const getters = {
  cleanSearchParams: (state) => {
    const cleanedData = {}

    Object.entries(state.searchParams).forEach(entry => {
      const originalKey = entry[0]
      // We don't want "Slow" to match "Very Slow" so use exact:
      const cleanKey = originalKey !== 'text' ? originalKey + '__exact' : originalKey
      let value = entry[1] // Could represenet a bool, or be an array of objects.

      if (value === null) {
        return
      }

      if (originalKey === 'tempo') {
        value = cleanTempo(value)
      }

      let cleanedValues = []

      if (Array.isArray(value)) {
        cleanedValues = value
      } else {
        cleanedValues = [value]
      }

      if (cleanedValues.length === 0) {
        return
      }

      // Now we need to go through array and pull out x.value if objects, or just return the thing
      cleanedValues = cleanedValues.map((i) => {
        if (i.hasOwnProperty('value')) {
          return i.value
        }
        return i
      })

      cleanedData[cleanKey] = cleanedValues.join(',')
    })

    return cleanedData
  },

  multiselectSearchOptions: (state) => {
    return state.searchOptions.filter(item => item.type === 'multiselect')
  },

  booleanSearchOptions: (state) => {
    return state.searchOptions.filter(item => item.type === 'boolean')
  },

  searchOptions: (state) => {
    return state.searchOptions
  },

  searchParams: (state) => {
    return state.searchParams
  },

  searchTags: (state) => {
    const tags = []
    const vForKeys = []

    Object.entries(state.searchParams).forEach(entry => {
      const key = entry[0]
      let value = entry[1]
      let fieldType
      if (key !== 'text') {
        // console.log(key) // getting key not working, maybe because operation to add in instrumental is wrong
        fieldType = state.searchOptions.find(item => item.field === key).type
      }

      if (key === 'tempo') {
        value = cleanTempo(value)
        return
      }

      if (Array.isArray(value)) {
        if (value.length === 0) {
          return
        }
        for (const x of value) {
          const vForKey = key + x.value
          if (vForKeys.indexOf(vForKey) < 0) {
            tags.push({ label: x.label, value: x.value, type: fieldType, field: key })
            vForKeys.push(vForKey)
          }
        }
      } else if (value !== null) {
        const vForKey = key + value
        if (vForKeys.indexOf(vForKey) < 0) {
          tags.push({ label: value.label, value: value.value, type: fieldType, field: key })
          vForKeys.push(vForKey)
        }
      }
    })

    return tags
  },

  isOptionValueSelected: (state) => (searchOption, optionValue) => {
    const searchField = searchOption.field
    const valueField = searchOption.filter_field // E.g. `name` or `pk`
    const searchValue = optionValue[valueField] // E.g. "Bass" or `123`

    const param = state.searchParams[searchField]
    if (param === undefined) {
      // This happens when using the track page search bar and non-existent search fields
      // are being checked.
      return false
    }
    // `param` could be an object or array of objects.
    if (Array.isArray(param)) {
      for (let i = 0; i < param.length; i++) {
        const item = param[i]
        if (item.value === searchValue) {
          return true
        }
      }
      return false
    } else {
      return param.value === searchValue
    }
  },

  isBooleanOptionSelected: (state) => (searchOption) => {
    const searchField = searchOption.field
    const onValue = searchOption.value

    const param = state.searchParams[searchField]
    if (param === undefined || param === null) {
      return false
    }
    return param.value === onValue
  },

  loading: (state) => {
    return state.loading
  },

  loadingNewSearch: (state) => {
    return state.loadingNewSearch
  }
}

// Incremented per doSearch call so responses to superseded requests are ignored.
let searchRequestId = 0

// actions
const actions = {
  // `analytics` ({ source, session }) goes with a new search's request for
  // the backend's search log; a later page's next link carries it along.
  doSearch: (context, analytics = {}) => {
    // Only the latest request may update results: a slower earlier response
    // (e.g. before a filter change) would otherwise overwrite newer results or
    // append a stale next page to them. Resolves false for a superseded request.
    const requestId = ++searchRequestId
    const isCurrent = () => requestId === searchRequestId
    const isNewSearch = context.state.nextPageUrl === null

    // Now either load the first page, or the next page of results.
    const request = isNewSearch
      ? searchClient.searchTracks({ ...context.getters.cleanSearchParams, ...analytics })
      : client.get(context.state.nextPageUrl)

    return request
      .then(response => {
        if (!isCurrent()) {
          return false
        }
        const results = response.data.results.map(track =>
          normalizeTrackSearchResult(track)
        )
        context.commit(SET_NEXT_PAGE_URL, response.data.next)
        context.commit(isNewSearch ? SET_RESULTS : APPEND_RESULTS, results)
        if (context.state.results.length > 0) {
          context.dispatch('player/loadTracks', context.state.results, { root: true })
        }
        return true
      })
      .catch(error => {
        if (!isCurrent()) {
          return false
        }
        throw error
      })
  },

  initSearchParams: (context) => {
    return searchClient.searchOptionsTrack().then(response => {
      context.commit(SET_SEARCH_OPTIONS, response.data)
      context.dispatch('resetSearchParams')
    })
  },

  resetSearchParams: (context) => {
    context.commit(SET_SEARCH_PARAMS, {})
    context.commit(SET_A_SEARCH_PARAM, {
      field: 'text',
      value: []
    })

    context.state.searchOptions.forEach(item => {
      const key = item.field

      if (item.type === 'boolean') {
        context.commit(SET_A_SEARCH_PARAM, {
          field: key,
          value: null
        })
      } else {
        context.commit(SET_A_SEARCH_PARAM, {
          field: key,
          value: []
        })
      }
    })
  },

  setNextPageUrl: (context, value) => {
    context.commit(SET_NEXT_PAGE_URL, value)
  },

  setSearchParam: (context, payload) => {
    context.commit(SET_A_SEARCH_PARAM, payload)
  },

  appendSearchParam: (context, payload) => {
    context.commit(APPEND_A_SEARCH_PARAM, payload)
  },

  removeValueFromParams: (context, { field, value }) => {
    let newArray = context.state.searchParams[field]
    newArray = newArray.filter(function (item) {
      return item.value !== value
    })
    context.commit(SET_A_SEARCH_PARAM, {
      field,
      value: newArray
    })
  },

  setLoading: (context, value) => {
    context.commit(SET_LOADING, value)
  },

  setLoadingNewSearch: (context, value) => {
    context.commit(SET_LOADING_NEW_SEARCH, value)
  },

  handleMultiOptionToggle: (context, { searchOption, toggledValue }) => {
    const searchField = searchOption.field
    const valueField = searchOption.filter_field // E.g. `name` or `pk`
    const searchValue = toggledValue[valueField] // E.g. "Bass" or `123`
    const label = toggledValue.name

    const valueIsSelected = context.state.searchParams[searchField].find(item => item.value === searchValue)

    if (valueIsSelected) {
      const newArray = context.state.searchParams[searchField].filter(function (item) {
        return item.value !== searchValue
      })
      context.commit(SET_A_SEARCH_PARAM, {
        field: searchField,
        value: newArray
      })
      return false // `false` means we removed the option
    } else {
      context.dispatch('appendSearchParam', {
        field: searchField,
        label,
        value: searchValue
      })
      return true // `true` means we added the option
    }
  },

  handleBooleanOptionToggle: (context, searchOption) => {
    const searchField = searchOption.field
    const optionValue = searchOption.value
    const label = searchOption.label

    const currentSearchParam = context.state.searchParams[searchField]
    let currentValue

    if (currentSearchParam !== null) {
      currentValue = currentSearchParam.value
    }

    if (optionValue !== currentValue) {
      context.dispatch('setSearchParam', {
        field: searchField,
        value: { label, value: optionValue }
      })
      return true // `true` means we added the option
    } else {
      context.dispatch('setSearchParam', {
        field: searchField,
        value: null
      })
      return false // `false` means we added the option
    }
  }
}

// mutations
const mutations = {
  [SET_SEARCH_OPTIONS]: (state, payload) => {
    state.searchOptions = payload
  },

  [SET_RESULTS]: (state, items) => {
    Vue.set(state, 'results', items)
  },

  [APPEND_RESULTS]: (state, items) => {
    state.results.push(...items)
  },

  [SET_NEXT_PAGE_URL]: (state, value) => {
    Vue.set(state, 'nextPageUrl', value)
  },

  [SET_SEARCH_PARAMS]: (state, payload) => {
    Vue.set(state, 'searchParams', payload)
  },

  [SET_A_SEARCH_PARAM]: (state, { field, value }) => {
    Vue.set(state.searchParams, field, value)
  },

  [APPEND_A_SEARCH_PARAM]: (state, { field, label, value }) => {
    state.searchParams[field].push({ label, value })
  },

  [SET_LOADING]: (state, payload) => {
    state.loading = payload
  },

  [SET_LOADING_NEW_SEARCH]: (state, payload) => {
    state.loadingNewSearch = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
